/**
 * Utility functions for managing comma-separated list fields
 * Converts between string storage format and array format for UI
 */

/**
 * Parse a comma-separated string into an array of trimmed items
 * Handles both string and array inputs
 */
export function parseListField(value: any): string[] {
  if (!value) return [];

  // If already an array, return it
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter((item) => item.length > 0);
  }

  // If string, split by comma
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  }

  return [];
}

/**
 * Convert an array of items to a comma-separated string for storage
 */
export function arrayToListString(items: string[]): string {
  if (!Array.isArray(items)) return "";
  return items
    .map((item) => String(item).trim())
    .filter((item) => item.length > 0)
    .join(", ");
}

/**
 * Convert a comma-separated string to an array for storage
 * This is what gets sent to the database
 */
export function listStringToArray(value: string): string[] {
  return parseListField(value);
}

/**
 * Validate a single list item
 */
export function validateListItem(item: string): { valid: boolean; error?: string } {
  const trimmed = item.trim();

  if (trimmed.length === 0) {
    return { valid: false, error: "Item cannot be empty" };
  }

  if (trimmed.length > 100) {
    return { valid: false, error: "Item cannot exceed 100 characters" };
  }

  // Check for invalid characters (commas not allowed in items)
  if (trimmed.includes(",")) {
    return { valid: false, error: "Item cannot contain commas" };
  }

  return { valid: true };
}

/**
 * Validate all items in a list
 */
export function validateListItems(items: string[]): {
  valid: boolean;
  errors: Record<string, string>;
} {
  const errors: Record<string, string> = {};
  let hasErrors = false;

  items.forEach((item, index) => {
    const validation = validateListItem(item);
    if (!validation.valid) {
      errors[`item-${index}`] = validation.error || "Invalid item";
      hasErrors = true;
    }
  });

  return { valid: !hasErrors, errors };
}

/**
 * Add an item to a list, avoiding duplicates
 */
export function addToList(items: string[], newItem: string): string[] {
  const trimmed = newItem.trim();
  if (!trimmed || items.includes(trimmed)) {
    return items;
  }
  return [...items, trimmed];
}

/**
 * Remove an item from a list
 */
export function removeFromList(items: string[], itemToRemove: string): string[] {
  return items.filter((item) => item !== itemToRemove);
}

/**
 * Update an item in a list
 */
export function updateListItem(items: string[], oldItem: string, newItem: string): string[] {
  const trimmed = newItem.trim();
  if (!trimmed) return items;

  return items.map((item) => (item === oldItem ? trimmed : item));
}
